import { STORAGE_KEY } from "@constants";
import { markPwaInstallRewardPendingIfNewInstall } from "@utils/profileBadgeDownloadRow";
import { getRuntimeContext } from "@utils/pwa";
import type { BeforeInstallPromptEvent } from "kz-ui-sdk";
import { getPersistedDueAt } from "kz-ui-sdk";
import moment from "moment";
import { useEffect } from "react";
import { setCapturedPrompt } from "./useCapturedInstallPrompt";

// profile-badge-download-row: keeps its own copy of the install prompt for the Profile card. The shared copy
// can't be used: PwaInstallInterceptor skips the prompt while the banner is snoozed (72h after ✕), and
// AppInstaller clears it from the store on ✕. Mount next to PwaInstallInterceptor so the one-time
// `beforeinstallprompt` isn't missed.
const InstallPromptCapture = () => {
  useEffect(() => {
    // Installed-app open of a new install (iOS/external have no `appinstalled`).
    if (getRuntimeContext() === "pwa") {
      markPwaInstallRewardPendingIfNewInstall();
    }

    const handlePrompt = (event: Event) => {
      // Always keep the latest event: a prompt can only be shown once, so a re-fired one replaces the used one.
      setCapturedPrompt(event as BeforeInstallPromptEvent);
      const dueAt = getPersistedDueAt(STORAGE_KEY.PWA_REMINDER);
      if (dueAt && moment().isBefore(dueAt)) {
        // Banner snoozed. Chrome re-fires the event after the card's install dialog is dismissed, and
        // AppInstaller would store it and bring the banner back, so no other listener gets it (capture runs first).
        // Nothing else needs it during the snooze: PwaInstallInterceptor ignores it (preventDefault is its job,
        // done here instead), the Profile download row isn't rendered, and PwaInstallActionHost reads the store.
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    // Browser install (Android/desktop): the app claims on its first open.
    const handleInstalled = () => {
      setCapturedPrompt(null);
      markPwaInstallRewardPendingIfNewInstall();
    };
    window.addEventListener("beforeinstallprompt", handlePrompt, { capture: true });
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handlePrompt, { capture: true });
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  return null;
};

export default InstallPromptCapture;
